import {
  queryOptions,
  skipToken,
  useQueries,
  useQuery
} from '@tanstack/react-query'
import { SeksjonResponse } from '../types/types'
import { useAuth } from './useAuth'
import { OwnerId } from '../utils/taskUtils'
import { throwOnError } from '../utils/throwOnError'

type AuthFetch = ReturnType<typeof useAuth>['authFetch']

export const getSeksjonerOptions = (
  authFetch: AuthFetch,
  avdelingId?: string
) => {
  return queryOptions({
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    queryKey: ['seksjoner', avdelingId],
    queryFn:
      avdelingId == null
        ? skipToken
        : async () => {
            const { data, error } = await authFetch().GET(
              '/orgenhet-api/avdelinger/{avdelingid}/seksjoner',
              {
                params: {
                  path: {
                    avdelingid: avdelingId
                  }
                }
              }
            )
            if (error) {
              throwOnError(error)
            }
            const response: SeksjonResponse = data as SeksjonResponse
            return response._embedded?.seksjonList ?? []
          }
  })
}

export const useGetSeksjonerQueries = (avdelingOwnerIds: OwnerId[]) => {
  const { authFetch } = useAuth()
  return useQueries({
    queries: avdelingOwnerIds
      .map((ownerId) => ownerId.owner)
      .map((avdelingId) => getSeksjonerOptions(authFetch, avdelingId)),
    combine: (results) => {
      return {
        data: results.flatMap((result) =>
          Array.isArray(result.data) ? result.data : []
        ),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        error: results.find((result) => result.error != null)?.error
      }
    }
  })
}

export const useGetSeksjonerQuery = (avdelingId?: string) => {
  const { authFetch } = useAuth()
  return useQuery(getSeksjonerOptions(authFetch, avdelingId))
}
